import { useEffect, useRef, useState } from 'react'
import {
  ArrowLeft,
  CheckCircle2,
  Database,
  FileText,
  Loader2,
  Pencil,
  Plug,
  Sparkles,
  Upload,
  X,
} from 'lucide-react'
import Modal from '../Modal'
import Button from '../forms/Button'

type AddPayorMethod = 'contract' | 'sync' | 'manual'

type AddPayorMethodModalProps = {
  open: boolean
  onClose: () => void
  onProceedManual: () => void
  onProceedFromContract: (files: File[]) => void
  onProceedFromSync: (system: string) => void
}

type Step = 'choose' | 'upload' | 'sync'

const SYNC_SYSTEMS: { id: string; name: string; description: string }[] = [
  {
    id: 'availity',
    name: 'Availity',
    description: 'Multi-payor clearinghouse and provider portal',
  },
  {
    id: 'change-healthcare',
    name: 'Change Healthcare',
    description: 'EDI clearinghouse and revenue cycle platform',
  },
  {
    id: 'waystar',
    name: 'Waystar',
    description: 'Clearinghouse, claims, and remits',
  },
  {
    id: 'athena',
    name: 'Athenahealth',
    description: 'Practice management and billing system',
  },
  {
    id: 'epic',
    name: 'Epic',
    description: 'EHR and practice management',
  },
  {
    id: 'custom-api',
    name: 'Custom API',
    description: 'Connect any system using a payor REST API',
  },
]

const MAX_UPLOAD_BYTES = 25 * 1024 * 1024 // 25 MB
const ACCEPTED_TYPES = ['.pdf', '.doc', '.docx', '.txt']

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

function MethodCard({
  icon,
  title,
  description,
  badge,
  onClick,
}: {
  icon: React.ReactNode
  title: string
  description: string
  badge?: { label: string; tone: 'primary' | 'neutral' }
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative w-full text-left rounded-xl border border-gray-200 bg-white p-4 sm:p-5 transition-all hover:border-primary-400 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
    >
      <div className="flex items-start gap-4">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600 group-hover:bg-primary-100">
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h4 className="text-sm sm:text-base font-semibold text-gray-900">{title}</h4>
            {badge && (
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                  badge.tone === 'primary'
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {badge.tone === 'primary' && <Sparkles size={10} />}
                {badge.label}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-gray-500 leading-relaxed">{description}</p>
        </div>
      </div>
    </button>
  )
}

const AddPayorMethodModal = ({
  open,
  onClose,
  onProceedManual,
  onProceedFromContract,
  onProceedFromSync,
}: AddPayorMethodModalProps) => {
  const [step, setStep] = useState<Step>('choose')
  const [files, setFiles] = useState<File[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [analyzing, setAnalyzing] = useState(false)
  const [selectedSystem, setSelectedSystem] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      setStep('choose')
      setFiles([])
      setIsDragging(false)
      setUploadError(null)
      setAnalyzing(false)
      setSelectedSystem(null)
    }
  }, [open])

  const choose = (method: AddPayorMethod) => {
    if (method === 'manual') {
      onProceedManual()
      return
    }
    if (method === 'contract') {
      setStep('upload')
      return
    }
    setStep('sync')
  }

  const acceptFiles = (incoming: FileList | File[]) => {
    setUploadError(null)
    const next: File[] = []
    for (const file of Array.from(incoming)) {
      const ext = `.${file.name.split('.').pop()?.toLowerCase() ?? ''}`
      if (!ACCEPTED_TYPES.includes(ext)) {
        setUploadError(`Unsupported file type: ${file.name}. Use PDF, DOC, DOCX, or TXT.`)
        continue
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        setUploadError(`${file.name} is larger than 25 MB.`)
        continue
      }
      next.push(file)
    }
    if (next.length > 0) {
      setFiles((prev) => [...prev, ...next])
    }
  }

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const handleAnalyze = () => {
    if (files.length === 0) return
    setAnalyzing(true)
    window.setTimeout(() => {
      setAnalyzing(false)
      onProceedFromContract(files)
    }, 900)
  }

  const handleConnectSystem = () => {
    if (!selectedSystem) return
    const system = SYNC_SYSTEMS.find((s) => s.id === selectedSystem)
    onProceedFromSync(system?.name ?? selectedSystem)
  }

  const title =
    step === 'choose'
      ? 'Add payor'
      : step === 'upload'
        ? 'Upload contract'
        : 'Sync from existing system'

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={title}
      size={step === 'choose' ? 'lg' : 'lg'}
    >
      {step === 'choose' && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Choose how you'd like to configure this payor. You can extract details from an existing
            contract, sync from a connected system, or enter everything manually.
          </p>
          <div className="space-y-3">
            <MethodCard
              icon={<FileText size={20} />}
              title="Create from contract"
              description="Upload a payor contract and let Contract Intelligence extract payor details, fee schedules, and policy rules automatically."
              badge={{ label: 'AI assisted', tone: 'primary' }}
              onClick={() => choose('contract')}
            />
            <MethodCard
              icon={<Database size={20} />}
              title="Sync from existing system"
              description="Pull payor data from your clearinghouse, EHR, or billing platform via API. Keep records in sync automatically."
              onClick={() => choose('sync')}
            />
            <MethodCard
              icon={<Pencil size={20} />}
              title="Enter manually"
              description="Configure payor details, plans, fee schedules, and billing rules yourself using the step-by-step form."
              onClick={() => choose('manual')}
            />
          </div>
        </div>
      )}

      {step === 'upload' && (
        <div className="space-y-4">
          <button
            type="button"
            onClick={() => setStep('choose')}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft size={14} />
            Back to options
          </button>

          <p className="text-sm text-gray-600">
            Drop one or more payor contracts below. We'll extract payor details, fee schedules,
            and policy rules from each contract so you only need to review and confirm. You'll
            review them one at a time.
          </p>

          <div
            onDragOver={(e) => {
              e.preventDefault()
              setIsDragging(true)
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(e) => {
              e.preventDefault()
              setIsDragging(false)
              if (e.dataTransfer.files?.length) acceptFiles(e.dataTransfer.files)
            }}
            className={`rounded-xl border-2 border-dashed transition-colors p-6 sm:p-8 text-center ${
              isDragging
                ? 'border-primary-500 bg-primary-50/60'
                : 'border-gray-300 bg-gray-50/60 hover:bg-gray-50'
            }`}
          >
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-white border border-gray-200 text-primary-600">
              <Upload size={20} />
            </div>
            <p className="mt-3 text-sm font-medium text-gray-900">
              Drag and drop your contract here
            </p>
            <p className="mt-1 text-xs text-gray-500">
              PDF, DOC, DOCX, or TXT • Up to 25 MB per file
            </p>
            <div className="mt-4">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                accept={ACCEPTED_TYPES.join(',')}
                onChange={(e) => {
                  if (e.target.files?.length) acceptFiles(e.target.files)
                  e.target.value = ''
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                Browse files
              </Button>
            </div>
          </div>

          {uploadError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {uploadError}
            </div>
          )}

          {files.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Selected files
              </p>
              <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
                {files.map((file, index) => (
                  <li
                    key={`${file.name}-${index}`}
                    className="flex items-center gap-3 px-3 py-2.5"
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                      <FileText size={16} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                      <p className="text-xs text-gray-500">{formatBytes(file.size)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="text-gray-400 hover:text-gray-700 p-1"
                      aria-label={`Remove ${file.name}`}
                    >
                      <X size={16} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-2 border-t border-gray-200">
            <p className="text-xs text-gray-500 inline-flex items-center gap-1.5">
              <Sparkles size={12} className="text-primary-500" />
              Contract Intelligence will extract fields you can review before saving.
            </p>
            <div className="flex items-center gap-2 sm:justify-end">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleAnalyze}
                disabled={files.length === 0 || analyzing}
                className="inline-flex items-center gap-1.5"
              >
                {analyzing ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Analyzing{files.length > 1 ? ` ${files.length} contracts` : ''}…
                  </>
                ) : (
                  <>
                    <Sparkles size={16} />
                    Analyze &amp; continue
                    {files.length > 1 && (
                      <span className="ml-1 inline-flex items-center rounded-full bg-white/20 px-1.5 py-0.5 text-[10px] font-semibold">
                        {files.length}
                      </span>
                    )}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {step === 'sync' && (
        <div className="space-y-4">
          <button
            type="button"
            onClick={() => setStep('choose')}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft size={14} />
            Back to options
          </button>

          <p className="text-sm text-gray-600">
            Select the system you'd like to pull payor records from. We'll prefill the payor form
            with data fetched from that connection.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {SYNC_SYSTEMS.map((system) => {
              const active = selectedSystem === system.id
              return (
                <button
                  key={system.id}
                  type="button"
                  onClick={() => setSelectedSystem(system.id)}
                  className={`flex items-start gap-3 rounded-lg border p-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 ${
                    active
                      ? 'border-primary-500 bg-primary-50/60'
                      : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${
                      active ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    <Plug size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {system.name}
                      </p>
                      {active && <CheckCircle2 size={14} className="text-primary-600" />}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 leading-snug">
                      {system.description}
                    </p>
                  </div>
                </button>
              )
            })}
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-2 border-t border-gray-200">
            <p className="text-xs text-gray-500">
              You can adjust the synced fields before saving the payor.
            </p>
            <div className="flex items-center gap-2 sm:justify-end">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleConnectSystem}
                disabled={!selectedSystem}
              >
                Connect &amp; continue
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  )
}

export default AddPayorMethodModal
